import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { TrendingUp, DollarSign, CheckCircle2, Clock } from "lucide-react";
import { useCurrency } from "@/hooks/use-currency";

export const ClientAnalytics = () => {
  const { formatAmount, convertAmount, currency } = useCurrency();
  const [stats, setStats] = useState({
    totalRequests: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalInvestment: 0,
  });

  useEffect(() => {
    fetchStats();
  }, [currency]); // Re-fetch when currency changes

  const fetchStats = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch service requests count
      const { count: requestsCount } = await supabase
        .from("service_requests")
        .select("*", { count: "exact", head: true })
        .eq("client_id", user.id);

      // Fetch projects
      const { data: projects } = await supabase
        .from("projects")
        .select("status, budget, currency")
        .eq("user_id", user.id);

      const activeCount = projects?.filter((p) => p.status === "active").length || 0;
      const completedCount = projects?.filter((p) => p.status === "completed").length || 0;
      
      // Convert all project budgets to display currency (default to USD if currency not set)
      const totalBudget = projects?.reduce((sum, p) => {
        if (!p.budget) return sum;
        const sourceCurrency = (p.currency as any) || "USD";
        const converted = convertAmount(p.budget, sourceCurrency, currency);
        return sum + converted;
      }, 0) || 0;

      setStats({
        totalRequests: requestsCount || 0,
        activeProjects: activeCount,
        completedProjects: completedCount,
        totalInvestment: totalBudget,
      });
    } catch (error) {
      console.error("Error fetching stats:", error);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 md:gap-6">
        <Card className="glass-card-hover p-4 sm:p-6 transition-all hover-scale">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-lg bg-primary/10 shrink-0">
              <TrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-primary" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Total Requests</p>
              <p className="text-xl sm:text-2xl font-bold">{stats.totalRequests}</p>
            </div>
          </div>
        </Card>

        <Card className="glass-card-hover p-4 sm:p-6 transition-all hover-scale">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-lg bg-green-500/10 shrink-0">
              <Clock className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Active Projects</p>
              <p className="text-xl sm:text-2xl font-bold">{stats.activeProjects}</p>
            </div>
          </div>
        </Card>

        <Card className="glass-card-hover p-4 sm:p-6 transition-all hover-scale">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-lg bg-blue-500/10 shrink-0">
              <CheckCircle2 className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Completed</p>
              <p className="text-xl sm:text-2xl font-bold">{stats.completedProjects}</p>
            </div>
          </div>
        </Card>

        <Card className="glass-card-hover p-4 sm:p-6 transition-all hover-scale">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2 sm:p-3 rounded-lg bg-purple-500/10 shrink-0">
              <DollarSign className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
            </div>
            <div className="min-w-0">
              <p className="text-xs sm:text-sm text-muted-foreground truncate">Total Investment</p>
              <p className="text-xl sm:text-2xl font-bold truncate">{formatAmount(stats.totalInvestment)}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Additional Analytics Section */}
      <Card className="glass-card p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold mb-4">Project Overview</h3>
        <div className="space-y-3 sm:space-y-4">
          <div className="flex justify-between items-center gap-4">
            <span className="text-sm sm:text-base text-muted-foreground">Success Rate</span>
            <span className="text-sm sm:text-base font-bold shrink-0">
              {stats.totalRequests > 0 
                ? `${Math.round((stats.completedProjects / stats.totalRequests) * 100)}%`
                : "0%"
              }
            </span>
          </div>
          <div className="flex justify-between items-center gap-4">
            <span className="text-sm sm:text-base text-muted-foreground">Average Project Budget</span>
            <span className="text-sm sm:text-base font-bold shrink-0">
              {stats.completedProjects > 0
                ? formatAmount(stats.totalInvestment / stats.completedProjects)
                : formatAmount(0)
              }
            </span>
          </div>
          <div className="flex justify-between items-center gap-4">
            <span className="text-sm sm:text-base text-muted-foreground">Projects In Progress</span>
            <span className="text-sm sm:text-base font-bold shrink-0">{stats.activeProjects}</span>
          </div>
        </div>
      </Card>
    </div>
  );
};
